import { defineNuxtPlugin } from 'nuxt/app'
import { useAuthState } from '#imports'
import { BackendClients, createBackendClients } from '~/api/backendClients'
import { BackendClientKey, PremiseCheckerKey, WebsocketClientKey } from '~/api/symbols'
import { SwitchingPremiseChecker } from '~/api/switchingPremiseChecker'
import { createWebsocketClient, WebsocketClient } from '~/api/websocket'
import { watchImmediate } from '@vueuse/core'
import { mapNotifyType, Notify, Progress } from '~/lib/notify'
import { NUXT_PROXY_PREFIX, SocketIoPath } from '~/lib/constants'
import { joinURL } from 'ufo'

declare module 'vue' {
  interface ComponentCustomProperties {
    $backend: BackendClients
    $ws: WebsocketClient
    $premiseChecker: SwitchingPremiseChecker
  }
}
declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $backend: BackendClients
    $ws: WebsocketClient
    $premiseChecker: SwitchingPremiseChecker
  }
}

const SchaltungstoolBackendPlugin = defineNuxtPlugin((nuxtApp) => {
  const auth = useAuthState()
  /**
   * WS Client
   */
  const websocketURL = new URL(joinURL(window.location.origin, NUXT_PROXY_PREFIX, SocketIoPath))
  const websocket = createWebsocketClient(auth.token, websocketURL)
  // handle backend notifications
  websocket.on('notification', ({ type, message }) => Notify(message, { type: mapNotifyType(type) }))
  websocket.on('progress', (notification) => Progress.from(notification))
  // connect on token change
  watchImmediate(auth.token, (token) => {
    if (websocket.connected.value) websocket.disconnect()
    if (token) {
      websocket.connect()
    } else {
      websocket.disconnect()
    }
  })

  /**
   * Inject $backend clients
   */
  const clients = createBackendClients(() => auth.token.value ?? '')
  nuxtApp.provide('backend', clients)
  nuxtApp.vueApp.provide(BackendClientKey, clients as BackendClients)

  /**
   * Inject $premiseChecker
   */
  const premiseChecker = new SwitchingPremiseChecker(clients.switchingPremises)
  nuxtApp.provide('premiseChecker', premiseChecker)
  nuxtApp.vueApp.provide(PremiseCheckerKey, premiseChecker)

  /**
   * Inject $ws
   */
  nuxtApp.provide('ws', websocket)
  nuxtApp.vueApp.provide(WebsocketClientKey, websocket)
})
export default SchaltungstoolBackendPlugin
